import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  Box,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Avatar,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  Layers as LayersIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const createEmptyItem = () => ({
  name: '',
  price_adjustment: 0,
  quanty: 0,
  imageFile: null,
  imagePreview: ''
});

const createEmptyGroup = () => ({
  name: '',
  isRequired: false,
  min_selection: 0,
  max_selection: 1,
  items: [createEmptyItem()]
});

export default function ProductVariationsManagerUX({ productUuid = '' }) {
  const [uuid, setUuid] = useState(productUuid);
  const [expandedGroup, setExpandedGroup] = useState(0);
  const [variacoes, setVariacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Limpeza de URLs de preview para evitar Memory Leak ao desmontar o componente
  useEffect(() => {
    return () => {
      variacoes.forEach((group) => {
        group.items.forEach((item) => {
          if (item.imagePreview) {
            URL.revokeObjectURL(item.imagePreview);
          }
        });
      });
    };
  }, [variacoes]);

  // --- Feedback / Notificações ---
  const showNotification = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- Manipulação dos Grupos ---
  const handleAccordionChange = (panelIndex) => (_, isExpanded) => {
    setExpandedGroup(isExpanded ? panelIndex : false);
  };

  const handleAddGroup = () => {
    const newGroup = createEmptyGroup();
    setVariacoes((prev) => [...prev, newGroup]);
    setExpandedGroup(variacoes.length);
  };

  const handleRemoveGroup = (groupIndex) => {
    setVariacoes((prev) => {
      const groupToRemove = prev[groupIndex];
      groupToRemove?.items.forEach((item) => {
        if (item.imagePreview) URL.revokeObjectURL(item.imagePreview);
      });
      return prev.filter((_, index) => index !== groupIndex);
    });
  };

  const handleGroupChange = (groupIndex, field, value) => {
    setVariacoes((prev) => {
      const updated = [...prev];
      updated[groupIndex] = { ...updated[groupIndex], [field]: value };
      return updated;
    });
  };

  const handleToggleRequired = (groupIndex, isChecked) => {
    setVariacoes((prev) => {
      const updated = [...prev];
      updated[groupIndex] = {
        ...updated[groupIndex],
        isRequired: isChecked,
        min_selection: isChecked ? 1 : 0
      };
      return updated;
    });
  };

  // --- Manipulação dos Itens ---
  const handleAddItem = (groupIndex) => {
    setVariacoes((prev) => {
      const updated = [...prev];
      updated[groupIndex].items = [...updated[groupIndex].items, createEmptyItem()];
      return updated;
    });
  };

  const handleRemoveItem = (groupIndex, itemIndex) => {
    setVariacoes((prev) => {
      const updated = [...prev];
      const item = updated[groupIndex].items[itemIndex];

      if (item.imagePreview) {
        URL.revokeObjectURL(item.imagePreview);
      }

      updated[groupIndex].items = updated[groupIndex].items.filter(
        (_, index) => index !== itemIndex
      );
      return updated;
    });
  };

  const handleItemChange = (groupIndex, itemIndex, field, value) => {
    setVariacoes((prev) => {
      const updated = [...prev];
      const groupItems = [...updated[groupIndex].items];
      groupItems[itemIndex] = { ...groupItems[itemIndex], [field]: value };
      updated[groupIndex].items = groupItems;
      return updated;
    });
  };

  // --- Upload e Remoção de Imagem ---
  const handleImageUpload = (groupIndex, itemIndex, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showNotification('A imagem deve ter no máximo 2MB.', 'warning');
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    setVariacoes((prev) => {
      const updated = [...prev];
      const currentItem = updated[groupIndex].items[itemIndex];

      if (currentItem.imagePreview) {
        URL.revokeObjectURL(currentItem.imagePreview);
      }

      updated[groupIndex].items[itemIndex] = {
        ...currentItem,
        imageFile: file,
        imagePreview: previewUrl
      };

      return updated;
    });
  };

  const handleRemoveImage = (groupIndex, itemIndex, event) => {
    event.stopPropagation();
    setVariacoes((prev) => {
      const updated = [...prev];
      const currentItem = updated[groupIndex].items[itemIndex];

      if (currentItem.imagePreview) {
        URL.revokeObjectURL(currentItem.imagePreview);
      }

      updated[groupIndex].items[itemIndex] = {
        ...currentItem,
        imageFile: null,
        imagePreview: ''
      };

      return updated;
    });
  };

  // --- Submit do Formulário ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!uuid.trim()) {
      showNotification('Informe o código identificador (UUID) do produto.', 'error');
      return;
    }

    if (variacoes.length === 0) {
      showNotification('Adicione pelo menos um grupo de opções para o produto.', 'warning');
      return;
    }

    // Validação de Integridade Regra de Negócio
    for (let g = 0; g < variacoes.length; g++) {
      const group = variacoes[g];
      if (Number(group.min_selection) > Number(group.max_selection)) {
        showNotification(
          `No grupo "${group.name || `#${g + 1}`}", a seleção mínima não pode ser maior que a máxima.`,
          'error'
        );
        return;
      }
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('uuid', uuid.trim());

      // Estrutura do payload estruturado
      const variacoesPayload = variacoes.map((group) => ({
        name: group.name,
        min_selection: Number(group.min_selection) || 0,
        max_selection: Number(group.max_selection) || 1,
        items: group.items.map((item) => ({
          name: item.name,
          price_adjustment: parseFloat(item.price_adjustment) || 0,
          quanty: parseInt(item.quanty, 10) || 0
        }))
      }));

      formData.append('variacoes', JSON.stringify(variacoesPayload));

      // Anexar arquivos de imagem mantendo índice de referência
      variacoes.forEach((group, gIdx) => {
        group.items.forEach((item, iIdx) => {
          if (item.imageFile) {
            formData.append(`image_${gIdx}_${iIdx}`, item.imageFile);
          }
        });
      });

      const response = await fetch('/products/set/v2/variations', {
        method: 'PUT',
        body: formData // Envio real via Multipart/FormData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao salvar as opções do produto.');
      }

      showNotification(data.message || 'Variações salvas com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao enviar variações:', error);
      showNotification(error.message || 'Erro de conexão ao salvar alterações.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: { xs: 1, sm: 2 } }}>
      <Paper elevation={2} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
        {/* Cabeçalho */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main">
            Opções e Variações do Produto
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Configure opções personalizadas como <b>Tamanho</b>, <b>Cor</b> ou <b>Ingredientes Adicionais</b>.
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          {/* Card UUID do Produto */}


          {/* Estado Vazio (Empty State) */}
          {variacoes.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 2,
                border: '2px dashed #cbd5e1',
                borderRadius: 3,
                mb: 4,
                backgroundColor: '#fafafa'
              }}
            >
              <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" fontWeight="600">
                Nenhuma variação adicionada a este produto.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Clique no botão abaixo para criar seu primeiro grupo de opções.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddGroup}
                sx={{ borderRadius: 2 }}
              >
                Adicionar Grupo
              </Button>
            </Box>
          )}

          {/* Lista de Grupos (Accordions) */}
          {variacoes.map((group, groupIdx) => (
            <Accordion
              key={groupIdx}
              expanded={expandedGroup === groupIdx}
              onChange={handleAccordionChange(groupIdx)}
              sx={{
                mb: 2,
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: '#f1f5f9', px: 3 }}
              >
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {group.name.trim() || `Grupo sem nome #${groupIdx + 1}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pr: 2 }}>
                    <Chip
                      size="small"
                      label={group.isRequired ? 'Obrigatório' : 'Opcional'}
                      color={group.isRequired ? 'error' : 'default'}
                    />
                    <Chip
                      size="small"
                      label={`${group.items.length} item(ns)`}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 3 }}>
                {/* Configurações Globais do Grupo */}
                <Grid container spacing={2.5} alignItems="center" sx={{ mb: 3 }}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Nome do Grupo de Opções"
                      placeholder="Ex: Escolha o Tamanho, Ponto da Carne"
                      value={group.name}
                      onChange={(e) => handleGroupChange(groupIdx, 'name', e.target.value)}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={4} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={group.isRequired}
                          onChange={(e) => handleToggleRequired(groupIdx, e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Obrigatório?"
                    />
                  </Grid>

                  <Grid item xs={5} sm={3} md={1.5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Min. Seleção"
                      value={group.min_selection}
                      inputProps={{ min: 0 }}
                      onChange={(e) => handleGroupChange(groupIdx, 'min_selection', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={5} sm={3} md={1.5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max. Seleção"
                      value={group.max_selection}
                      inputProps={{ min: 1 }}
                      onChange={(e) => handleGroupChange(groupIdx, 'max_selection', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={2} sm={2} md={1} sx={{ textAlign: 'right' }}>
                    <Tooltip title="Excluir este grupo">
                      <IconButton color="error" onClick={() => handleRemoveGroup(groupIdx)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Lista de Itens do Grupo */}
                <Box>
                  <Typography variant="subtitle2" color="primary.main" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Opções / Itens deste grupo:
                  </Typography>

                  {group.items.map((item, itemIdx) => (
                    <Card
                      key={itemIdx}
                      variant="outlined"
                      sx={{ p: 2, mb: 1.5, backgroundColor: '#ffffff', borderRadius: 2 }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        {/* Imagem do Item */}
                        <Grid item xs={12} sm={2} md={1.5} sx={{ textAlign: 'center' }}>
                          <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            <input
                              accept="image/*"
                              style={{ display: 'none' }}
                              id={`image-upload-${groupIdx}-${itemIdx}`}
                              type="file"
                              onChange={(e) => handleImageUpload(groupIdx, itemIdx, e)}
                            />
                            <label htmlFor={`image-upload-${groupIdx}-${itemIdx}`}>
                              <Tooltip title="Clique para enviar ou trocar foto">
                                <Avatar
                                  src={item.imagePreview}
                                  variant="rounded"
                                  sx={{
                                    width: 56,
                                    height: 56,
                                    cursor: 'pointer',
                                    margin: '0 auto',
                                    border: '1px dashed #94a3b8',
                                    bgcolor: '#f8fafc',
                                    '&:hover': { opacity: 0.8 }
                                  }}
                                >
                                  <CloudUploadIcon color="action" />
                                </Avatar>
                              </Tooltip>
                            </label>
                            {item.imagePreview && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleRemoveImage(groupIdx, itemIdx, e)}
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  backgroundColor: 'error.main',
                                  color: 'white',
                                  p: '2px',
                                  '&:hover': { backgroundColor: 'error.dark' }
                                }}
                              >
                                <CloseIcon fontSize="small" style={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Grid>

                        {/* Nome do Item */}
                        <Grid item xs={12} sm={4} md={4.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Nome do Item"
                            placeholder="Ex: Grande, Molho Especial, Azul"
                            value={item.name}
                            onChange={(e) =>
                              handleItemChange(groupIdx, itemIdx, 'name', e.target.value)
                            }
                            required
                          />
                        </Grid>

                        {/* Acréscimo (Preço) */}
                        <Grid item xs={6} sm={2.5} md={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Acréscimo (R$)"
                            inputProps={{ step: '0.01' }}
                            value={item.price_adjustment}
                            onChange={(e) =>
                              handleItemChange(groupIdx, itemIdx, 'price_adjustment', e.target.value)
                            }
                          />
                        </Grid>

                        {/* Quantidade / Estoque */}
                        <Grid item xs={4} sm={2.5} md={2.5}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Estoque"
                            value={item.quanty}
                            inputProps={{ min: 0 }}
                            onChange={(e) =>
                              handleItemChange(groupIdx, itemIdx, 'quanty', e.target.value)
                            }
                          />
                        </Grid>

                        {/* Botão Deletar Item */}
                        <Grid item xs={2} sm={1} md={1} sx={{ textAlign: 'right' }}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveItem(groupIdx, itemIdx)}
                            disabled={group.items.length === 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItem(groupIdx)}
                    sx={{ mt: 1 }}
                  >
                    Adicionar Nova Opção
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Botões de Ação */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mt: 4
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddGroup}
              fullWidth={{ xs: true, sm: false }}
            >
              Novo Grupo de Variações
            </Button>

            <Button
              type="submit"
              variant="contained"
              color="success"
              size="large"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
              }
              fullWidth={{ xs: true, sm: false }}
              sx={{ px: 4, fontWeight: 'bold' }}
            >
              {loading ? 'Salvar Variações...' : 'Salvar Alterações'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Snackbar / Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}